import {
  Button,
  ButtonVariantEnum,
} from "@booking/components/atoms/forms/Button";
import { ImageProviderEnum, ImageVariantEnum, SizeEnum } from "@booking/enums";
import {
  faArrowPointer,
  faGolfBallTee,
  faMedal,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DEFAULT_LABEL_ICON, LABEL_ICONS } from "@search/api/constants";
import { type SearchResult } from "@search/api/types";
import { PackageDetails } from "@search/components/compounds/PackageDetails";
import { Location } from "@search/components/molecules/Location";
import { Price } from "@search/components/molecules/Price";
import { Rating } from "@search/components/molecules/Rating";
import { customPluralize } from "@search/utils/helpers";
import { useWindowSize } from "@uidotdev/usehooks";
import clsx from "clsx";
import pluralize from "pluralize";

import { type ImageProps } from "@/components/atoms/media/Image";
import { Figure } from "@/components/molecules/media/Figure";
import { ColorEnum } from "@/enums";
import { type CommonProps } from "@/types";
import { containerCSSVars, setCSSVariables } from "@/utils/helpers";
import { gaClickAttrs } from "@/utils/tracking";

import styles from "./ResultCard.module.css";

const ref = "result-card";

interface ResultCardProps extends Partial<CommonProps> {
  details: SearchResult;
  dateParam: string;
  flightsParam?: boolean;
  nightsParam?: number;
  flightsFilter?: boolean;
}

function generateSlug(
  slug: string,
  date: string,
  nights?: number,
  anchor: string = "",
  packageId?: number,
  flights?: boolean,
  groupSize?: number,
) {
  if (!date && !packageId && !nights && !flights && !groupSize) {
    return `${slug}${anchor}`;
  }

  const params: {
    arrival_date?: string;
    has_flight?: string;
    package?: string;
    nights?: string;
    group_size?: string;
  } = {};
  if (date) {
    params.arrival_date = date;
  }
  if (flights) {
    params.has_flight = "true";
  }
  if (packageId) {
    params.package = packageId.toString();
  }
  if (nights && nights > 0) {
    params.nights = nights.toString();
  }
  if (groupSize && groupSize > 0) {
    params.group_size = groupSize.toString();
  }

  const search = new URLSearchParams(params).toString();
  return `${slug}?${search}${anchor}`;
}

function useResultCard(props: ResultCardProps) {
  const { details, dateParam, flightsParam, nightsParam } = props;
  const { labels = [], transportation, venue, featured } = details;
  const {
    breadcrumbs = [],
    distance,
    image,
    rating = 0,
    ratingCount = 0,
    totalPackageCount = 0,
  } = venue;
  const className = clsx(
    styles[ref],
    featured && styles["feature-card"],
    props.className,
  );

  if (image) {
    image.provider = ImageProviderEnum.CLOUDINARY;
    image.variant = ImageVariantEnum.SEARCH;
  }

  const vars = containerCSSVars(useWindowSize(), image);
  let transport = "";
  if (transportation.flights && props.flightsFilter) {
    transport = "Flights, ";
  } else if (transportation.eurotunnel) {
    transport = "Le Shuttle, ";
  }

  return {
    className,
    image: image as ImageProps,
    labels: labels.map((label) => {
      const icon =
        LABEL_ICONS[label.slug as keyof typeof LABEL_ICONS] ||
        DEFAULT_LABEL_ICON;

      return { text: label.name, icon };
    }),
    transport,
    totalPackageCount,
    rating,
    ratingCount,
    vars: setCSSVariables(vars),
    venue: {
      breadcrumbs,
      distance,
      countryCode: venue.countryCode,
      name: venue.name,
      packageSlug: generateSlug(
        venue.slug,
        dateParam,
        nightsParam,
        "#offers",
        details.packageId,
        flightsParam,
        details.groupSize,
      ),
      rating,
      ratingCount,
      totalPackageCount,
      venueSlug: generateSlug(
        venue.slug,
        dateParam,
        nightsParam,
        "",
        details.packageId,
        flightsParam,
      ),
    },
  };
}

function ResultCard(props: ResultCardProps) {
  const { className, image, labels, vars, venue, transport } =
    useResultCard(props);
  const { details } = props;
  const {
    nights,
    price,
    priceWithFlights,
    rounds,
    featured,
    bookOnline,
    fourBall,
  } = details;
  const displayPrice =
    props.flightsFilter && priceWithFlights ? priceWithFlights : price;

  const {
    breadcrumbs,
    distance = 0,
    countryCode,
    name,
    packageSlug,
    rating,
    ratingCount,
    totalPackageCount,
    venueSlug,
  } = venue;

  return (
    <article className={className} style={vars}>
      {featured && (
        <aside className={styles["feature-aside"]}>
          <FontAwesomeIcon icon={faMedal} />
          <span>Featured</span>
        </aside>
      )}

      <header>
        <Button
          color={ColorEnum.DARKBLUE}
          variant={ButtonVariantEnum.PLAIN}
          href={venueSlug}
          target="_blank"
          {...gaClickAttrs({
            eventGroup: "Result card",
            click: "find_out_more_click",
          })}
        >
          {name}
        </Button>

        {(bookOnline || fourBall || labels.length > 0) && (
          <div className={styles["labels-container"]}>
            {bookOnline && (
              <div className={styles["label-bookable"]}>
                <FontAwesomeIcon icon={faArrowPointer} />
                <span>Bookable Online</span>
              </div>
            )}

            {fourBall && (
              <div className={styles["label-fourball"]}>
                <FontAwesomeIcon icon={faGolfBallTee} />
                <span>4-ball offer</span>
              </div>
            )}

            {labels.map((item, index) => (
              <div key={`label_${index}`} className={styles["label-offer"]}>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        )}

        <Location countryCode={countryCode} location={breadcrumbs} />

        {distance > 0 && (
          <span>{`${pluralize("mile", distance, true)} away`}</span>
        )}

        <Rating count={ratingCount} rating={rating} />
      </header>

      <section>
        <p>
          {transport}
          {`${customPluralize("Night", nights, true)}, ${customPluralize("Round", rounds, true)}`}
        </p>

        <PackageDetails
          {...props.details}
          flightsFilter={props.flightsFilter}
        />
      </section>

      <Figure
        image={{ ...image }}
        href={venueSlug}
        target="_blank"
        {...gaClickAttrs({ eventGroup: "Result card", click: "image_click" })}
      />

      <footer>
        <Price amount={displayPrice} baseline groupSize={details.groupSize} />
        <Button
          color={ColorEnum.DARKGREEN}
          href={packageSlug}
          size={SizeEnum.LARGE}
          variant={ButtonVariantEnum.SOLID}
          target="_blank"
          {...gaClickAttrs({
            eventGroup: "Result card",
            click: "find_out_more_click",
          })}
        >
          See all {pluralize("holiday", totalPackageCount, true)}
        </Button>
      </footer>
    </article>
  );
}

export default ResultCard;
export { ResultCard, type ResultCardProps };
